import { useParams } from "react-router-dom";

import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Ring } from "../../components/Ring";
import { NavRow, RowGroup } from "../../components/Rows";
import { Tag } from "../../components/Tag";
import { DOW_LABELS, dayLabel, parseDay, todayStr } from "../../domain/date";
import { familyExtrasLine, familyTraining, familyWalksLine, familyWhoLine, HOW_WAS_WORDS, pronouns } from "../../domain/family";
import { sessionFor, sessionsPerWeek } from "../../domain/plan";
import { mealWalkWords, programmeFor, programmeWeekNo } from "../../domain/programme";
import { sessionWhere, steadyFigures, steadyWeekRows, steadyWeekTitle, weekSpan, type SteadyDayRow } from "../../domain/programmeView";
import type { KayaState } from "../../domain/types";
import { weekDone, weekRows } from "../../domain/week";
import { completedWeek } from "../../domain/weekDone";
import { WeekTicks } from "../../components/WeekTicks";
import { useLinked } from "../../lib/linked";
import { LinkedShell, familyPath, setByWords, useMyName } from "./linkedPage";
import "../Train.css";

const STRENGTH = ["SB", "CH", "GC"];

/** A day's second line: where it happens and how it went, or plainly that it was not done; then the
 *  yoga extra and the day's walks, which count on any day, a rest day too (ADR-068). */
function daySub(S: KayaState, r: SteadyDayRow, today: string): string | undefined {
  const own = sessionSub(S, r, today);
  const extra = familyExtrasLine(S, r.ds);
  const first = own && extra ? `${own} · ${extra}` : (own ?? (extra ? extra.charAt(0).toUpperCase() + extra.slice(1) : undefined));
  const walks = familyWalksLine(S, r.ds);
  return [first, walks].filter(Boolean).join(" · ") || undefined;
}

function sessionSub(S: KayaState, r: SteadyDayRow, today: string): string | undefined {
  if (r.rest) return undefined;
  if (r.isToday && !r.done) return r.started ? "today, started" : "today, not started";
  if (!r.done && r.ds < today) return "not done";
  const where = STRENGTH.includes(r.key) ? sessionWhere(sessionFor(r.key, S).blocks) : mealWalkWords(S);
  const fit = r.done ? S.logs[r.ds]?.howWas?.fit : undefined;
  const parts = [where || null, fit ? HOW_WAS_WORDS[fit] : null].filter((x): x is string => !!x);
  return parts.length ? parts.join(" · ") : undefined;
}

/**
 * You › Family › one person › their week (board FamilyWeek; ADR-056): the week as TrainWeek draws
 * it on their phone, read-only — the template per day, its minutes, a tick when done and an empty
 * mark when not, never red, with How was it? beside a done day. No Start and no Change on the
 * days: the days come from their exercise answers, which are changed on the answers page.
 */
export default function FamilyWeek() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  const me = useMyName();
  const today = todayStr();

  return (
    <LinkedShell q={q} title={(name) => (name ? `${name}’s week` : "Their week")} back={{ to: familyPath(id, "training"), label: "Back" }}>
      {({ S }) => {
        const p = pronouns(S);
        const f = familyTraining(S, today);
        const steady = f.programme !== null && programmeFor(S, today) === "steady";
        const rows = steadyWeekRows(S, today);
        const fig = steadyFigures(S, today);
        const kaya = weekDone(weekRows(S, today));
        const at = S.profile.training?.at;
        const by = setByWords(f.setBy, me);
        const walks = mealWalkWords(S);

        return (
          <>
            <Hero
              label={
                <>
                  {steady ? "Steady" : "Kaya"}
                  {typeof at === "number" ? ` · from ${dayLabel(todayStr(new Date(at)))}` : ""}
                  {by ? (
                    <>
                      {" "}
                      <Tag tone="neutral">{by}</Tag>
                    </>
                  ) : null}
                </>
              }
              title={steady ? steadyWeekTitle(S, today) : `${sessionsPerWeek(S)} sessions a week`}
              line={
                [
                  steady
                    ? `Week ${programmeWeekNo(S, today)} · ${fig.strengthDone} of ${fig.strengthOf} strength days done`
                    : `${kaya.done} of ${kaya.of} done this week`,
                  familyWhoLine(f),
                ]
                  .filter(Boolean)
                  .join(" · ")
              }
            />
            <WeekTicks week={completedWeek(S, today)} />

            <RowGroup heading={`${weekSpan(today)} · ${fig.plannedMin} min planned`}>
              {rows.map((r) => (
                <NavRow
                  key={r.ds}
                  kind="value"
                  title={`${DOW_LABELS[parseDay(r.ds).getDay()]} · ${r.name}`}
                  subtitle={daySub(S, r, today)}
                  icon={
                    r.done ? (
                      I.check
                    ) : r.rest ? (
                      I.minus
                    ) : (
                      <span className="train-ring">
                        <Ring pct={r.pct} size={22} stroke={3} color="var(--brand)" />
                      </span>
                    )
                  }
                  tone={r.done ? "success" : "default"}
                  value={r.minutes !== null ? `${r.minutes} min` : undefined}
                />
              ))}
            </RowGroup>

            <p className="you-fine">
              {[
                steady ? "Strength days never back to back" : null,
                walks ? `walks ${walks}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>

            <RowGroup>
              <NavRow
                icon={I.edit}
                title={`${p.Their} days and minutes`}
                subtitle={`From ${p.their} exercise answers — change them there`}
                to={familyPath(id, "training-answers")}
              />
            </RowGroup>
          </>
        );
      }}
    </LinkedShell>
  );
}
